/**
 * The API's response envelope, read without a schema library (`SITE-SPD04`).
 *
 * `lib/api.ts` is on every page, and reading this one shape through zod put
 * zod (12 KB on the wire) in the script every page loads. This accepts and
 * refuses exactly what that schema did (`lib/api-envelope.test.ts` holds the
 * schema as its oracle): an object with a boolean `success`; `data` anything;
 * `error`, when present, an object with a string `code` and `message` and its
 * other keys kept; `meta`, when present, an object whose `requestId` and
 * `timestamp` are strings when present, and nothing else kept. Other top-level
 * keys are dropped.
 */
export type ParsedEnvelope = {
  success: boolean;
  data?: unknown;
  error?: { code: string; message: string; details?: unknown; [key: string]: unknown };
  meta?: { requestId?: string; timestamp?: string };
};

export class EnvelopeError extends Error {
  constructor(where: string) {
    super(`The API answered with a malformed envelope (${where})`);
    this.name = "EnvelopeError";
  }
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function optionalString(record: Record<string, unknown>, key: string, where: string): string | undefined {
  const value = record[key];
  if (value === undefined) return undefined;
  if (typeof value !== "string") throw new EnvelopeError(`${where}.${key}`);
  return value;
}

export function parseEnvelope(raw: unknown): ParsedEnvelope {
  if (!isObject(raw)) throw new EnvelopeError("body");
  if (typeof raw["success"] !== "boolean") throw new EnvelopeError("success");
  const out: ParsedEnvelope = { success: raw["success"] };
  if ("data" in raw) out.data = raw["data"];

  const error = raw["error"];
  if (error !== undefined) {
    if (!isObject(error)) throw new EnvelopeError("error");
    if (typeof error["code"] !== "string") throw new EnvelopeError("error.code");
    if (typeof error["message"] !== "string") throw new EnvelopeError("error.message");
    out.error = { ...error, code: error["code"], message: error["message"] };
  }

  const meta = raw["meta"];
  if (meta !== undefined) {
    if (!isObject(meta)) throw new EnvelopeError("meta");
    const requestId = optionalString(meta, "requestId", "meta");
    const timestamp = optionalString(meta, "timestamp", "meta");
    out.meta = {
      ...(requestId !== undefined ? { requestId } : {}),
      ...(timestamp !== undefined ? { timestamp } : {}),
    };
  }
  return out;
}
